import {
  deleteV1CompaniesCompanyIdFuelPriceIndicesId,
  getV1CompaniesCompanyIdFuelPriceIndices,
  GetV1CompaniesCompanyIdFuelPriceIndicesData,
  getV1CompaniesCompanyIdFuelPriceIndicesTypeahead,
  GetV1CompaniesCompanyIdFuelPriceIndicesTypeaheadData,
  patchV1CompaniesCompanyIdFuelPriceIndicesId,
  postV1CompaniesCompanyIdFuelPriceIndices,
} from '@treadinc/horizon-api-spec';

import connection from '~services/connectionModule';
import { extractPagination } from '~services/pagination';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { FuelPriceIndex } from './models';

interface GetCompanyFuelPriceIndicesParams {
  limit?: number;
  cursor?: string;
}

interface CreateFuelPriceIndexParams {
  name: string;
}

interface UpdateFuelPriceIndexParams {
  id: string;
  name: string;
}

interface GetFuelPriceIndicesTypeaheadParams {
  searchQuery?: string;
  cursor?: string;
}

export function useFuelPriceIndices() {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const getCompanyFuelPriceIndices = async ({
    limit = 20,
    cursor,
  }: GetCompanyFuelPriceIndicesParams) => {
    const query = {
      'page[limit]': limit,
      ...(cursor && { 'page[after]': cursor }),
    } as GetV1CompaniesCompanyIdFuelPriceIndicesData['query'];

    try {
      const response = await getV1CompaniesCompanyIdFuelPriceIndices({
        path: { 'company-id': companyId },
        query,
      });
      const parsedData = response.data.data.map(FuelPriceIndex.parse);
      const pagination = extractPagination(response);
      return { data: parsedData, pagination };
    } catch (error) {
      connection.handleRequestError(error);
      throw error;
    }
  };

  const createFuelPriceIndex = async ({ name }: CreateFuelPriceIndexParams) => {
    try {
      const response = await postV1CompaniesCompanyIdFuelPriceIndices({
        path: { 'company-id': companyId },
        body: { name },
      });
      return FuelPriceIndex.parse(response.data.data);
    } catch (error) {
      connection.handleRequestError(error);
      throw error;
    }
  };

  const updateFuelPriceIndex = async ({ id, name }: UpdateFuelPriceIndexParams) => {
    try {
      const response = await patchV1CompaniesCompanyIdFuelPriceIndicesId({
        path: { 'company-id': companyId, id },
        body: { name },
      });
      return FuelPriceIndex.parse(response.data.data);
    } catch (error) {
      connection.handleRequestError(error);
      throw error;
    }
  };

  const deleteFuelPriceIndex = async (id: string) => {
    try {
      await deleteV1CompaniesCompanyIdFuelPriceIndicesId({
        path: { 'company-id': companyId, id },
      });
    } catch (error) {
      connection.handleRequestError(error);
      throw error;
    }
  };

  const getFuelPriceIndicesTypeahead = async ({
    searchQuery,
    cursor,
  }: GetFuelPriceIndicesTypeaheadParams) => {
    try {
      const query: GetV1CompaniesCompanyIdFuelPriceIndicesTypeaheadData['query'] = {
        'page[limit]': 20,
      };

      if (searchQuery) {
        query['search[query]'] = searchQuery;
      }

      if (cursor) {
        query['page[after]'] = cursor;
      }

      const response = await getV1CompaniesCompanyIdFuelPriceIndicesTypeahead({
        path: { 'company-id': companyId },
        query,
      });
      return response.data.data;
    } catch (error) {
      connection.handleRequestError(error);
      throw error;
    }
  };

  return {
    createFuelPriceIndex,
    deleteFuelPriceIndex,
    getCompanyFuelPriceIndices,
    getFuelPriceIndicesTypeahead,
    updateFuelPriceIndex,
  };
}
